import React from 'react';
import { ArrowRight, CheckCircle, Clock, DollarSign, Shield, Users } from 'lucide-react';
import plansImage from '../../assets/business-people-working-as-team-conference-room-plans-future_482257-25781.jpg_nur6h3.jpg';

const FlexiblePlansPage = () => {
  const plans = [
    {
      name: 'Basic Plan',
      features: [
        'Min Deposit: $500',
        'Max Deposit: $4,999',
        'Total ROI: 20%',
        'Investment Duration: 10 days',
        'Instant Withdrawal',
        '24/7 Customer Support'
      ],
      popular: false
    },
    {
      name: 'Standard Plan',
      features: [
        'Min Deposit: $5,999',
        'Max Deposit: $14,999',
        'Total ROI: 30%',
        'Investment Duration: 15 days',
        'Instant Withdrawal',
        '24/7 Customer Support'
      ],
      popular: true
    },
    {
      name: 'Business Plan',
      features: [
        'Min Deposit: $15,000',
        'Max Deposit: $49,999',
        'Total ROI: 40%',
        'Investment Duration: 20 days',
        'Instant Withdrawal',
        '24/7 Customer Support'
      ],
      popular: false
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Flexible Duration',
      description: 'Short-term investment options with flexible timeframes to suit your financial goals.'
    },
    {
      icon: DollarSign,
      title: 'Competitive Returns',
      description: 'Attractive interest rates designed to maximize your returns over shorter investment periods.'
    },
    {
      icon: Shield,
      title: 'Low Risk',
      description: 'Carefully managed investments with reduced risk exposure for capital preservation.'
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Professional guidance and 24/7 customer support throughout your investment journey.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src={plansImage}
            alt="Flexible Investment Plans"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">
            Flexible Investment Plans
          </h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / Pricing / <span className="text-gold-400">Flexible</span>
          </p>
        </div>
      </section>

      {/* Plans Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Flexible Investment Packages
            </h2>
            <p className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
              Flexible investments are designed for individuals seeking shorter-term growth opportunities 
              with the flexibility to adjust their investment strategy as needed. These investments 
              typically offer a shorter period of commitment, allowing investors to capitalize on market 
              opportunities within a relatively brief timeframe. They are well-suited for those who value 
              liquidity and the ability to access their funds within a shorter notice.
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {plans.map((plan, index) => (
              <div key={index} className={`relative bg-white rounded-2xl shadow-xl border-2 transition-all duration-300 hover:shadow-2xl ${
                plan.popular ? 'border-gold-500 transform scale-105' : 'border-gray-200 hover:border-gold-300'
              }`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gold-500 text-white px-6 py-2 rounded-full text-sm font-bold">
                      Most Popular
                    </div>
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center">
                    {plan.name}
                  </h3>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-gold-500 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a 
                    href="https://online.amdconsults.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-4 px-6 rounded-full font-bold transition-all duration-300 inline-block text-center ${
                      plan.popular 
                        ? 'bg-gold-500 hover:bg-gold-600 text-white shadow-lg' 
                        : 'bg-navy-900 hover:bg-navy-800 text-white'
                    }`}
                  >
                    Invest now
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h3 className="text-3xl md:text-4xl font-bold text-center text-navy-900 mb-12">
              Why Choose Flexible Plans?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="text-center group">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-3">
                      {benefit.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors duration-300">
              <span>Prev</span>
            </button>
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-gold-500 rounded-full"></div>
              <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            </div>
            <button className="flex items-center space-x-2 px-6 py-3 bg-gold-500 hover:bg-gold-600 text-white rounded-lg transition-colors duration-300">
              <span>Next</span>
            </button>
          </div>

          {/* CTA Section */}
          <div className="mt-20 bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Start Your Flexible Investment?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Explore diverse opportunities, compare features, and get expert advice every step of the way. 
              Your ideal investment is just a click away!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-full transition-colors duration-300"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300">
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlexiblePlansPage; 